import { useMemo } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { getListingEnergyInformation, resolveListingDataValue } from '@pkgs/components/utils/listingDataUtils';
import { type Listing } from '@pkgs/api/models';
import { Headline } from '@ev-tech/ui-core-components/Headline';

import { getEnergyContent } from '@/utils/energyInfoUtils';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';

import { PropertyFact } from './PropertyFact';
import { prepareDetailsColumns } from './prepareDetailsColumns';

import styles from './PropertyDetails.module.css';

export const PropertyDetailsEnergyInfo = ({ listing }: { listing: Listing }) => {
  const { t: tPropertyExpose } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { t: tListingData } = useTranslation(TRANSLATION_NAMESPACE.listing_data);
  const { language } = useSettings();

  const energyInfo = useMemo(() => {
    const descriptors = getListingEnergyInformation(listing, { language });
    const heatingMaterialDescriptors = descriptors.filter((descriptor) => descriptor.fieldName === 'heatingMaterial');
    const firstHeatingMaterialDescriptor = heatingMaterialDescriptors[0];

    return descriptors.flatMap((descriptor) => {
      if (descriptor.fieldName !== 'heatingMaterial') {
        return {
          label: tListingData(descriptor.labelKey),
          content: getEnergyContent({ descriptor, listing, t: tListingData }),
        };
      }

      if (descriptor !== firstHeatingMaterialDescriptor) return [];

      return {
        label: tListingData(descriptor.labelKey),
        content: heatingMaterialDescriptors.map((material) => resolveListingDataValue(material, tListingData)).join(', '),
      };
    });
  }, [language, listing, tListingData]);

  const [leftColumn, rightColumn] = prepareDetailsColumns(energyInfo);
  return (
    <div className={styles.detailsContainer}>
      <Headline size="h5" as="span" label={tPropertyExpose('search.expose.energyEfficiencyDetails.headline')} />
      <div className={styles.columnContainer}>
        <ul className={styles.list}>
          {leftColumn.map((item, index) => (
            <PropertyFact {...item} key={index} isLast={index === leftColumn.length - 1} />
          ))}
        </ul>
        <ul className={styles.list}>
          {rightColumn.map((item, index) => (
            <PropertyFact {...item} key={index} isLast={index === rightColumn.length - 1} />
          ))}
        </ul>
      </div>
    </div>
  );
};
